// client/src/lib/sentry.ts
import * as Sentry from "@sentry/react";
import { scrubSentryEvent, scrubSentryBreadcrumb } from "./sentryScrub";
import { isStacklessNetworkRejection } from "./sentryNoise";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || "development";

export function initSentry() {
  if (!SENTRY_DSN) {
    console.warn("⚠️ VITE_SENTRY_DSN not configured - error monitoring disabled");
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,

    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),
    ],

    // Performance monitoring - 10% sample rate
    tracesSampleRate: 0.1,

    // Only enable in non-development environments
    enabled: ENVIRONMENT !== "development",

    // Public token pages (/van/day/<token>, /v/s|c|r/<token>) carry a CREDENTIAL in the URL.
    // Scrub it from every event, transaction and breadcrumb before it leaves the browser.
    // A stackless unhandled "Load failed"/"Failed to fetch" is the device losing the network with
    // nothing identifying the caller — see sentryNoise.ts for why the rule is this narrow.
    beforeSend: (event) => (isStacklessNetworkRejection(event as any) ? null : scrubSentryEvent(event)),
    beforeSendTransaction: (event) => scrubSentryEvent(event),
    beforeBreadcrumb: (breadcrumb) => scrubSentryBreadcrumb(breadcrumb),
  });

  console.log(`✅ Sentry initialized (${ENVIRONMENT})`);
}

// Set user context when auth state changes
export function setSentryUser(user: { id: number; email: string; role: string } | null) {
  if (user) {
    Sentry.setUser({
      id: String(user.id),
      email: user.email,
      role: user.role,
    });
  } else {
    Sentry.setUser(null);
  }
}

export { Sentry };
